import { useSuspenseQuery } from "@tanstack/react-query";
import type React from "react";
import { createContext, useContext } from "react";
import { useTRPC } from "@/lib/trpc";

type Feature = { key: string; description: string; organizations: string[] };

interface FeaturesContextValue {
    features: Feature[];
    hasFeature: (key: string) => boolean;
}

const FeaturesContext = createContext<FeaturesContextValue | null>(null);

export const FeaturesProvider = ({ children }: { children: React.ReactNode }) => {
    const trpc = useTRPC();
    const { data: features } = useSuspenseQuery(
        trpc.features.list.queryOptions(undefined, {
            staleTime: Infinity,
            gcTime: Infinity,
        })
    );

    return (
        <FeaturesContext.Provider
            value={{
                features,
                hasFeature: (key: string) => features.some((x) => x.key === key),
            }}
        >
            {children}
        </FeaturesContext.Provider>
    );
};

export const useFeatures = () => {
    const ctx = useContext(FeaturesContext);
    if (!ctx) {
        throw new Error("useFeatures must be used within a FeaturesProvider");
    }
    return ctx;
};
